/*
 * Cart drawer "Auto-select upsells on add to cart".
 *
 * Enabled per Product upsells block, which renders data-auto-select="true" on its
 * cart-drawer-upsell elements. When the customer adds a product to the cart, every
 * eligible upsell of those blocks is switched on.
 *
 * Three paths, because the theme has three kinds of add to cart:
 *   1. Forms main.js owns that carry a .product-form__variants container (buy-buttons,
 *      separate ATC, bundles). Their upsells are switched on during the capture phase of
 *      the submit event, so main.js sends them inside the same /cart/add request.
 *   2. Forms main.js owns without that container (quick add cards). Their upsells are
 *      added one by one once the drawer has re-rendered.
 *   3. The hand written add to cart of snippets/product-buttons.liquid and
 *      snippets/product-buttons-buy-123.liquid, which never fires a submit event and posts
 *      its own JSON to /cart/add.js. Those call collectItems() below and append the result
 *      to their own items array, then rollback() if the request fails.
 */
(function () {
  if (window.cartUpsellAutoSelectLoaded) return;
  window.cartUpsellAutoSelectLoaded = true;

  var UPSELL_SELECTOR = 'cart-drawer-upsell[data-auto-select="true"][data-toggle="true"]';
  // Adds coming from the drawer itself or from another upsell/gift must not trigger the selection.
  var IGNORED_FORM_PARENTS =
    'cart-drawer, cart-drawer-gift, quantity-gifts, product-info-upsell, product-form[data-is-cart-upsell="true"]';

  // A pending add that never made the drawer re-render (failed request) is dropped instead of
  // riding on some unrelated render minutes later.
  var PENDING_TTL = 15000;

  var turnedOffByCustomer = [];
  var pendingHandles = [];
  var pendingSince = 0;
  var flushScheduled = false;
  var lastCollected = [];

  function getCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  function isInCart(cartDrawer, handle) {
    return !!cartDrawer.querySelector('.cart-item--product-' + handle);
  }

  /* Every auto-select upsell the customer has not switched off. */
  function getAutoSelectUpsells(cartDrawer) {
    return Array.prototype.filter.call(cartDrawer.querySelectorAll(UPSELL_SELECTOR), function (upsell) {
      return turnedOffByCustomer.indexOf(upsell.dataset.handle) === -1;
    });
  }

  /* Only the ones the submit path still has to switch on — main.js then sweeps up everything
     that ends up selected, including the ones preselected in the block settings. */
  function getEligibleUpsells(cartDrawer) {
    return getAutoSelectUpsells(cartDrawer).filter(function (upsell) {
      return upsell.dataset.selected !== 'true';
    });
  }

  /* Remember the upsells the customer switched off, so we don't switch them back on behind their back. */
  document.addEventListener(
    'click',
    function (event) {
      if (!event.target.closest) return;

      // main.js ignores those clicks too, they only change the variant.
      var nodeName = event.target.nodeName.toLowerCase();
      if (nodeName === 'select' || nodeName === 'option') return;

      var toggleBtn = event.target.closest('.upsell-toggle-btn');
      if (!toggleBtn) return;

      var upsell = toggleBtn.closest(UPSELL_SELECTOR);
      if (!upsell) return;

      var index = turnedOffByCustomer.indexOf(upsell.dataset.handle);
      if (upsell.dataset.selected === 'true') {
        if (index === -1) turnedOffByCustomer.push(upsell.dataset.handle);
      } else if (index !== -1) {
        turnedOffByCustomer.splice(index, 1);
      }
    },
    true
  );

  /* Capture phase runs before the submit listener main.js binds on the form itself. */
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form.matches || !form.matches('form[data-type="add-to-cart-form"]')) return;
      if (form.closest(IGNORED_FORM_PARENTS)) return;

      var cartDrawer = getCartDrawer();
      if (!cartDrawer) return;

      var upsells = getEligibleUpsells(cartDrawer);
      if (upsells.length === 0) return;

      if (form.querySelector('.product-form__variants')) {
        upsells.forEach(function (upsell) {
          upsell.dataset.selected = 'true';
        });
        return;
      }

      pendingHandles = upsells.map(function (upsell) {
        return upsell.dataset.handle;
      });
      pendingSince = Date.now();
    },
    true
  );

  /* Adds one pending upsell per drawer render, the way main.js cascades its gifts. */
  function flushPending() {
    if (pendingHandles.length === 0) return;

    var cartDrawer = getCartDrawer();
    if (!cartDrawer || Date.now() - pendingSince > PENDING_TTL) {
      pendingHandles = [];
      return;
    }

    while (pendingHandles.length > 0) {
      var handle = pendingHandles.shift();
      var upsell = cartDrawer.querySelector(UPSELL_SELECTOR + '[data-handle="' + handle + '"]');
      if (!upsell || typeof upsell.addToCart !== 'function') continue;

      upsell.dataset.selected = 'true';
      if (isInCart(cartDrawer, handle)) continue;

      // Its response re-renders the drawer, which flushes the next pending upsell.
      pendingSince = Date.now();
      upsell.addToCart();
      return;
    }
  }

  function scheduleFlush() {
    if (flushScheduled || pendingHandles.length === 0) return;
    flushScheduled = true;
    setTimeout(function () {
      flushScheduled = false;
      flushPending();
    });
  }

  function observeCartDrawer() {
    var cartDrawer = getCartDrawer();
    if (!cartDrawer) return;
    new MutationObserver(scheduleFlush).observe(cartDrawer, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeCartDrawer);
  } else {
    observeCartDrawer();
  }

  /*
   * Entry point for the add to cart flows that build their own /cart/add.js payload.
   * Switches the eligible upsells on and hands back the line items to append, so they are
   * added inside the caller's own request instead of a second one.
   */
  window.cartUpsellAutoSelect = {
    collectItems: function () {
      lastCollected = [];

      var cartDrawer = getCartDrawer();
      if (!cartDrawer) return [];

      var items = [];
      // Same rule main.js applies to its own forms: send every upsell that ends up selected
      // and is not in the cart yet. Skipping the ones already selected in the block settings
      // would make this page behave differently from a standard product form.
      getAutoSelectUpsells(cartDrawer).forEach(function (upsell) {
        var variantId = parseInt(upsell.dataset.id, 10);
        if (!variantId) return;

        // Remember what we overwrite, so rollback restores it instead of assuming 'false'.
        lastCollected.push({ handle: upsell.dataset.handle, selected: upsell.dataset.selected });

        // Switch the toggle on either way, the customer has to see what they are paying for.
        var alreadyInCart = isInCart(cartDrawer, upsell.dataset.handle);
        upsell.dataset.selected = 'true';

        if (!alreadyInCart) items.push({ id: variantId, quantity: 1 });
      });

      return items;
    },

    rollback: function () {
      var cartDrawer = getCartDrawer();

      if (cartDrawer) {
        lastCollected.forEach(function (entry) {
          // It is in the cart after all. The block hides the line item of a toggle upsell, so
          // switching the toggle off would leave the customer paying for something invisible.
          if (isInCart(cartDrawer, entry.handle)) return;

          // Re-queried instead of held onto: the drawer may have re-rendered in between.
          var upsell = cartDrawer.querySelector(UPSELL_SELECTOR + '[data-handle="' + entry.handle + '"]');
          if (upsell) upsell.dataset.selected = entry.selected === 'true' ? 'true' : 'false';
        });
      }

      lastCollected = [];
    },
  };
})();
